import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { type ColumnDef } from '@tanstack/react-table'
import { CircleAlert } from 'lucide-react'
import { DataTable } from '@/components/ui/data-table'
import { NameCell } from './TableCells'
import { statMetricColumns } from './statsColumns'
import { useStatLabel } from '../hooks/useStatLabel'
import {
  ADVERTISING_STAT_METRICS,
  useGroupedStats,
  type GroupedStatsRow,
} from '../hooks/useGroupedStats'
import type { BreakdownTableProps } from './TargetingTotalsTable'

/** The `site` object each `groupBy=SITE` chunk names itself with. */
interface SiteGroup {
  id: string
  name?: string
  siteUrl?: string
  [key: string]: unknown
}

type SiteStatsRow = GroupedStatsRow<SiteGroup>

/** Delivery per site, biggest first. */
export function SiteStatsTable({ query, range, maxHeight }: BreakdownTableProps) {
  const { t } = useTranslation()
  const { rows, isLoading, error } = useGroupedStats<SiteGroup>('SITE', 'site', query, range)
  const statLabel = useStatLabel()

  const columns = useMemo(
    (): ColumnDef<SiteStatsRow, unknown>[] => [
      {
        id: 'site',
        header: t('sites.title'),
        size: 220,
        meta: { label: t('sites.title') },
        accessorFn: (row) => row.group.name ?? row.group.id,
        cell: ({ row }) => (
          <NameCell name={row.original.group.name} sub={row.original.group.siteUrl} />
        ),
      },
      ...statMetricColumns<SiteStatsRow>({
        metrics: ADVERTISING_STAT_METRICS,
        statsFor: (row) => row.stats,
        label: statLabel,
      }),
    ],
    [t, statLabel]
  )

  if (error) {
    return (
      <div className="flex items-center gap-2 px-(--card-spacing) py-4 text-destructive">
        <CircleAlert className="size-4" />
        <span className="text-sm">{error.message}</span>
      </div>
    )
  }

  return (
    <DataTable
      columns={columns}
      data={rows}
      loading={isLoading}
      getRowId={(row) => row.group.id}
      emptyMessage={t('stats.noSiteStats')}
      columnsKey="breakdownSites"
      maxHeight={maxHeight}
      inset
    />
  )
}
